import { useEffect, useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { MailCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmailPage() {
  const { user, reload, logout } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wait, setWait] = useState(0);
  const [status, setStatus] = useState("checking");
  useEffect(() => { if (wait <= 0) return; const t = setTimeout(() => setWait(wait - 1), 1000); return () => clearTimeout(t); }, [wait]);
  useEffect(() => {
    api.post("/auth/resend-code?auto=true")
      .then(({ data }) => { setWait(data.wait || 0); setStatus("sent"); })
      .catch((e) => { setStatus("failed"); setError(errMsg(e)); });
  }, []);

  const submit = async (value = code) => {
    if (value.length !== 6) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/verify-email", { code: value });
      toast.success(data.user.plan?.source === "trial" ? "Email confirmed. Your 7-day Pro trial has started!" : "Email confirmed");
      await reload();
    } catch (e) { setError(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };
  const resend = async () => {
    try { const { data } = await api.post("/auth/resend-code"); toast.success("A new code is on its way"); setWait(data.wait || 60); setError(""); setStatus("sent"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center lf-glow lf-grain p-6" data-testid="verify-email-page">
      <div className="relative z-10 w-full max-w-md rounded-md border border-[#2D3442] bg-[#0F131A]/90 p-8 backdrop-blur-xl">
        <MailCheck className="h-8 w-8 text-[#F59E0B]" />
        <BrandLogo size="h-7 w-7" text="text-lg" testId="verify-brand" className="mt-6 mb-2" />
        <h1 className="font-display text-4xl text-[#F3EBDD]">Check your email</h1>
        <p className="mt-2 text-sm text-[#A8A090]" data-testid="verify-status">{status === "checking" ? "Sending a 6-digit code to" : status === "failed" ? "We couldn't send the code to" : "We sent a 6-digit code to"} <span className="text-[#F3EBDD]" data-testid="verify-email-address">{user?.email}</span>. Enter it below to open your archive{user?.plan?.trial_ends_at ? "" : " and start your free 7-day Pro trial"}.</p>
        <div className="mt-8 flex justify-center">
          <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v.replace(/\D/g, "")); if (v.length === 6) submit(v); }} disabled={busy} data-testid="verify-code-input" inputMode="numeric" autoFocus>
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
          </InputOTP>
        </div>
        {error && <p className="mt-4 text-center text-sm text-[#F87171]" data-testid="verify-error">{error}</p>}
        <Button className="mt-8 w-full" onClick={() => submit()} disabled={busy || code.length !== 6} data-testid="verify-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm email
        </Button>
        <div className="mt-5 flex items-center justify-between text-sm">
          <button onClick={resend} disabled={wait > 0} className="text-[#F59E0B] transition-opacity hover:underline disabled:cursor-not-allowed disabled:opacity-50" data-testid="verify-resend-button">
            {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
          </button>
          <button onClick={logout} className="text-[#A8A090] hover:text-[#F3EBDD]" data-testid="verify-logout-button">Use a different account</button>
        </div>
        <p className="mt-6 text-xs text-[#7A7468]">Can't find it? Check your spam folder. Codes expire after 15 minutes.</p>
      </div>
    </div>
  );
}
